import {requestStores} from '../../ListOfPREditors.brick.jsx';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Checkbox, Divider, Typography, Select} from '@mui/material';
import {PRFileUpload} from '../utils/PRFileUpload.jsx';
import {EZPRSelect} from '../utils/EZPRSelect.jsx';
import {complianceIndicatorOptions, exceptionOptions} from '../../_helpers';
import {AttachmentsTable} from './AttachmentsTable.jsx';
import {Attachment} from '../../../models/attachments_info/Attachment';
import PropTypes from 'prop-types';
import {getCBJustificationThresholdLambda} from '../../../utils/functionSignatures';
import {CurrencyController} from '../../../controllers/CurrencyController';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import MenuItem from '@mui/material/MenuItem';
import {OneTimeVendor} from '../../../models/OneTimeVendor';
import {LAMBDA_NAMES} from '../../../utils/constants';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {CBJustification.propTypes} props
 * @return {JSX.Element}
 */
export function CBJustification(props) {
    const useRequestStore = requestStores.get(props.prTag);

    const [selectedException, setSelectedException] = useRequestStore((state) => [state.selectedException, state.setSelectedException]);
    const createCBAttachment = useRequestStore((state) => state.createCBAttachment);
    const mainAttachments = useRequestStore((state) => state.mainAttachments);
    const deleteMainAttachment = useRequestStore((state) => state.deleteMainAttachment);
    const [hasException, setHasException] = useRequestStore((state) => [state.hasException, state.setHasException]);
    const [showCBJustification, setShowCBJustification] = useRequestStore((state) => [state.showCBJustification, state.setShowCBJustification]);
    const [isTotalGreaterThanThreshold, setTotalGreaterThanThreshold] = useRequestStore((state) => [state.isTotalGreaterThanThreshold, state.setTotalGreaterThanThreshold]);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const companyCode = useRequestStore((state) => state.companyCode);
    const getTotalPriceForBlankAndValidated = useRequestStore((state) => state.getTotalPriceForBlankAndValidated);
    const supplierStatus = useRequestStore((state) => state.supplierStatus);
    const conversionFailed = useRequestStore((state) => state.conversionFailed);
    const companyCodeCurrency = useRequestStore((state) => state.companyCodeCurrency);
    const setCompCodeCurrToThresholdCurrExchangeRate = useRequestStore((state) => state.setCompCodeCurrToThresholdCurrExchangeRate);
    const setCbThresholdCurrency = useRequestStore((state) => state.setCbThresholdCurrency);
    const supplierType = useRequestStore((state) => state.supplierType);
    const totalPriceForBlankAndValidated = getTotalPriceForBlankAndValidated().toFixed(2);
    const [complianceIndicator, setComplianceIndicator] = useRequestStore((state) => [state.complianceIndicator, state.setComplianceIndicator]);

    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});
    const cbExceptionDisabled = isDisabled('attachments.cbException');

    const getCBJustificationThresholdBrick = props.context.get(LAMBDA_NAMES.GET_CB_JUSTIFICATION_THRESHOLD_TOLER);

    const [threshold, setThreshold] = useState('');

    // The compliance indicator has been added in the store after version 2023-10-06, so we have this useEffect to
    // assign the right compliance indicator to the old PR.
    useEffect(() => {
        if (!complianceIndicator) {
            setComplianceIndicator(selectedException === exceptionOptions[2] ? complianceIndicatorOptions[1] : complianceIndicatorOptions[0]);
        }
    }, []);

    // This useEffect check if we need to display the CB Justification section, depending on the total amount of the PR
    useEffect(() => {
        // If currency conversion is failed and supplier status = Blank or Validated, display the CB Justification Threshold.
        if (conversionFailed) {
            setShowCBJustification(supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus);
            return;
        }
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;

        // eslint-disable-next-line require-jsdoc
        async function doEffect() {
            getCBJustificationThresholdLambda(
                props.context,
                getCBJustificationThresholdBrick,
                companyCode.value,
                supplierType === OneTimeVendor.supplierTypes.cmv || supplierType === OneTimeVendor.supplierTypes.otv || supplierType === OneTimeVendor.supplierTypes.mtv,
                userIdToCallSAPAPIs,
                async (resultThreshold) => {
                    if (!cleanupFunction) {
                        const parsedResultThreshold = JSON.parse(resultThreshold[0]);
                        let showCBSection;
                        // If there is only one threshold and only one exchange rate, do the currency conversion
                        if (parsedResultThreshold.length === 1) {
                            const cbThreshold = Number(parsedResultThreshold[0].Net_Price);
                            const cbCurrency = parsedResultThreshold[0].Currency;
                            setThreshold(`[${cbThreshold} ${cbCurrency}]`);

                            let totalPrice = getTotalPriceForBlankAndValidated();
                            if (!!companyCodeCurrency && companyCodeCurrency !== cbCurrency) {
                                const exchangeRate = await CurrencyController.getCurrencyExchangeRate(companyCodeCurrency, cbCurrency, userIdToCallSAPAPIs, props.context);
                                setCompCodeCurrToThresholdCurrExchangeRate(exchangeRate?.rate);
                                setCbThresholdCurrency(cbCurrency);
                                totalPrice = totalPrice * exchangeRate.rate;
                            } else {
                                setCompCodeCurrToThresholdCurrExchangeRate(1.0);
                                setCbThresholdCurrency(companyCodeCurrency);
                            }

                            setTotalGreaterThanThreshold(totalPrice >= cbThreshold);
                            // If price is above the threshold and supplier status = Blank or Validated, display the CB Justification Threshold
                            // Otherwise, do not display it
                            showCBSection = (totalPrice >= cbThreshold) && (supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus);
                        } else { // If there is no data (no threshold or no exchange rate), display the CB justification section
                            setThreshold('No threshold found');
                            showCBSection = (supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus);
                        }
                        setShowCBJustification(showCBSection);

                        if (showCBSection) {
                            const attachmentToMove = mainAttachments.find((a) => a.topic.toLowerCase() === 'event summary');
                            if (attachmentToMove) {
                                createCBAttachment(new Attachment(attachmentToMove.file, 'Event summary', attachmentToMove.user, attachmentToMove.date, attachmentToMove.name, attachmentToMove.userId));
                                deleteMainAttachment(mainAttachments.findIndex((a) => a.topic.toLowerCase() === 'event summary'));
                            }
                        }
                    }
                },
                () => {});
        }
        doEffect();
        return () => cleanupFunction = true;
    }, [companyCode, companyCodeCurrency, totalPriceForBlankAndValidated, conversionFailed, isTotalGreaterThanThreshold]);

    // If we hide the CB Justification, we set the hasException property to false
    // But not on first render, otherwise the exception information saved are lost
    const firstUpdate = useRef(true);
    useLayoutEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (!showCBJustification) {
            setHasException(false);
        }
    }, [showCBJustification]);


    const handleUploadCBAttachments = (attachment, resetInput) => {
        if (resetInput && typeof resetInput === 'function') {
            resetInput();
        }

        createCBAttachment(new Attachment(attachment.tag, '', attachment.user, attachment.date, attachment.name, attachment.userId));
    };

    const handleChangeComplianceIndicator = (event) => {
        setComplianceIndicator(event.target.value);
    };

    return showCBJustification ? (
        <>
            <Typography data-cy={'cb_attachment'} variant="h6" sx={{marginBottom: '1rem', color: 'rgb(75,66,70)', width: 'fit-content'}}>{`Competitive bidding justification - ${threshold}`}</Typography>
            <div className={'pr-editor-attachments-container'}>
                <Typography variant='h7' sx={{marginRight: '1rem'}}>Offers/Summary</Typography>
                <PRFileUpload
                    onFileUploaded={(att, resetInput) => {
                        handleUploadCBAttachments(att, resetInput);
                    }}
                    persistFiles={true}
                    disabled={isDisabled('attachments.cbUpload')}
                    fixedSize={true}
                />
                <Checkbox
                    checked={hasException}
                    onChange={() => setHasException(!hasException)}
                    disabled={cbExceptionDisabled}
                />
                <Typography
                    onClick={() => {
                        if (cbExceptionDisabled) return;
                        setHasException(!hasException);
                    }}
                    sx={{
                        marginRight: '2rem',
                        cursor: cbExceptionDisabled ? 'default' : 'pointer',
                        color: cbExceptionDisabled ? 'rgb(200,200,200)' : 'inherit',
                    }}
                >
                    Exception
                </Typography>
                {
                    <div className={`${hasException ? '' : 'pr-editor-attachments-exceptions-disabled'}`}
                        style={{width: '20rem'}}>
                        <EZPRSelect
                            name={'exception'}
                            options={exceptionOptions}
                            placeholder='Please choose the Exception'
                            value={selectedException}
                            onChange={(newValue) => {
                                setSelectedException(newValue);
                            }}
                            readOnly={isDisabled('attachments.cbExceptionReason')}
                        />
                    </div>
                }
            </div>
            <AttachmentsTable
                prTag={props.prTag}
                context={props.context}
                attachmentsType={'cb'}
                from='expandedPr'
                disabled={isDisabled('attachments.cbTable')}
            />
            <div className={'pr-editor-attachments-compliance-indicator'}>
                <div>Compliance indicator:</div>
                {props.currentTab === PurchaseRequest.tab.pendingSb ?
                    <Select
                        size={'small'}
                        value={complianceIndicator ?? ''}
                        onChange={handleChangeComplianceIndicator}
                    >
                        {complianceIndicatorOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select> :
                    <div>{complianceIndicator}</div>
                }
            </div>
            <Divider className={'pr-editor-attachments-horizontal-separator'}/>
        </>
    ) : null;
}

CBJustification.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    currentTab: PropTypes.string,
};
